import { InfinitiveList, useInfiniteScroll } from 'components/Molecules/List/index';
import SearchInput from 'components/Molecules/Search/SearchInput';
import { useList } from 'context/ResourceProvider/hooks/useList';
import React, { useMemo, useState, useEffect } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';
import SimpleCard from 'components/Molecules/Cards/SimpleCard';
import { List, notification } from 'antd';
import classes from './List.module.scss';
import { WithModal } from 'hocs/withModal';
import { DetailQualityRequirementTemplate } from '../Detail';
import { DeleteAction } from './DeleteAction';

const StyledList = styled(InfinitiveList)`
  max-height: 350px;
  margin-top: -24px;
`;

const PreviewModal = WithModal(DetailQualityRequirementTemplate);

export const ListQualityRequirementTemplate = ({
  onApplyTemplate,
  manageable = false,
  hasPreview = true,
}) => {
  const [previewRecord, setPreviewRecord] = useState(null);
  const [queryParams, setQueryParams] = useState({
    pagination: {
      pageNo: 1,
      pageSize: 10,
    },
    filters: {
      keyword: '',
    },
  });
  const [templates, setTemplates] = useState(new Map([]));
  const [pagination, setPagination] = React.useState({
    pageNo: 1,
    pageSize: 10,
  });

  const { data, loading, refetch } = useList({
    resource: 'quality-requirements/templates',
    ...queryParams,
  });

  const dataSource = get(data, 'data.data.list', []);
  const remotePagination = get(data, 'data.data.pagination', null);

  const allPageData = useMemo(() => {
    if (templates.size === 0) return [];
    return Array.from(templates.values()).reduce((acc, page) => {
      return [...acc, ...page];
    }, []);
  }, [templates]);

  const hasMore = useMemo(() => {
    if (remotePagination) {
      return remotePagination && allPageData.length < remotePagination.total;
    }
    return false;
  }, [remotePagination, allPageData.length]);

  const { loaderRef } = useInfiniteScroll({
    hasMore,
    isLoading: loading,
    loadMore: async () => {
      if (loading) return;
      setPagination((prev) => ({
        ...prev,
        pageNo: prev.pageNo + 1,
      }));
    },
  });

  const onView = (record) => () => {
    setPreviewRecord(record);
    // if (onViewTemplate) {
    //   onViewTemplate(record);
    // }
  };

  const onApply = (record) => () => {
    if (onApplyTemplate) {
      onApplyTemplate(record);
    }
  };

  useEffect(() => {
    if (dataSource.length > 0) {
      setTemplates((prev) => {
        const newMap = new Map(prev);
        newMap.set(pagination.pageNo, dataSource);
        return newMap;
      });
    }
  }, [dataSource]);

  return (
    <>
      <StyledList
        header={
          <SearchInput
            className={classes.searchInput}
            value={queryParams.filters.keyword}
            enterButton={false}
            onChange={(keyword) => {
              setQueryParams((prev) => {
                return {
                  ...prev,
                  filters: {
                    ...prev.filters,
                    keyword,
                  },
                  pagination: {
                    ...prev.pagination,
                    pageNo: 1,
                  },
                };
              });
            }}
            debounceTime={300}
          />
        }
        loaderRef={loaderRef}
        infinitiveProps={{
          hasMore,
          dataLength: (allPageData || []).length,
        }}
        dataSource={allPageData}
        renderItem={(item) => {
          return (
            <List.Item>
              <SimpleCard
                bordered={false}
                className={classes.item}
                title={item.templateName}
                actions={[
                  {
                    children: 'View',
                    type: 'primary',
                    ghost: true,
                    onClick: onView(item),
                    hidden: !hasPreview,
                  },
                  {
                    children: 'Remove',
                    type: 'primary',
                    ghost: true,
                    danger: true,
                    hidden: !manageable,
                    // Custom props Following DeleteAction Props.
                    id: item.id,
                    notificationSuccess: () => {
                      refetch();
                      notification.success({
                        message: 'Template deleted successfully',
                        placement: 'bottomLeft',
                      });
                    },
                    notificationError: () => {
                      notification.error({
                        message: 'Error deleting template',
                        placement: 'bottomLeft',
                      });
                    },
                    asSlot: DeleteAction,
                  },
                  {
                    children: 'Apply',
                    type: 'primary',
                    onClick: onApply(item),
                    hidden: manageable,
                  },
                ]}
              />
            </List.Item>
          );
        }}
        loading={loading}
      />
      <PreviewModal
        width={820}
        destroyOnClose
        title={`View ${previewRecord ? previewRecord.templateName : ''}`}
        id={previewRecord ? previewRecord.id : undefined}
        open={!!previewRecord}
        cancelButtonProps={{
          hidden: true,
        }}
        okText={manageable ? 'Close' : 'Apply'}
        onCancel={() => {
          setPreviewRecord(null);
        }}
        onOk={() => {
          setPreviewRecord(null);
          if (!manageable) {
            onApply(previewRecord)();
          }
        }}
      />
    </>
  );
};
